import { useState, memo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { ThemedMoreHorizIcon, ThemedMoreVertIcon } from "assets/CXFIcons/Actions";
import {
   openCredentialDialog,
   setCredentialDialogMode,
   setDeleteCredentialDialogOpen,
   setCredentialManagementShareUsersModalOpen,
   setCredentialManagementUnshareUsersModalOpen,
   setSelectedCredential
} from "redux/actions/portal/credentialManagementActionCreator";
import ProtectedButton from "authentication/ProtectedButton";
import { PERMISSIONS } from "constants/portal";
import { useCredentialPermissions } from "hooks/useCredentialPermissions";

const CredentialManagementAction = ({ credentialData, iconOrientation, isEditor }) => {
   const [anchorEl, setAnchorEl] = useState(null);
   const dispatch = useDispatch();
   const navigate = useNavigate();

   const { canOpen, canEdit, canShare, canDelete, shouldHideMenu } = useCredentialPermissions(credentialData, isEditor);

   if (shouldHideMenu || !canOpen) return null;

   const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
   const handleCloseMenu = () => setAnchorEl(null);

   const handleOpenCredentialManagementPage = () => {
      handleCloseMenu();
      window.open("/administration/credential-management", "_blank");
   };

   const handleActions = (actionMode) => {
      if (actionMode === "view") {
         handleCloseMenu();
         navigate(`/credential/${credentialData.id}`);
         return;
      }

      dispatch(setCredentialDialogMode(actionMode));
      dispatch(setSelectedCredential(actionMode === "add" ? {} : credentialData));

      switch (actionMode) {
         case "add":
         case "edit":
         case "view":
            dispatch(openCredentialDialog());
            break;
         case "delete":
            dispatch(setDeleteCredentialDialogOpen(true));
            break;
         case "share":
            dispatch(setCredentialManagementShareUsersModalOpen(true));
            break;
         case "unshare":
            dispatch(setCredentialManagementUnshareUsersModalOpen(true));
            break;
      }

      handleCloseMenu();
   };

   const open = Boolean(anchorEl);

   return (
      <>
         <IconButton aria-label="more" id="long-button" aria-controls={open ? "credential-management-menu" : undefined} aria-expanded={open ? "true" : undefined} aria-haspopup="true" onClick={handleOpenMenu}>
            <Tooltip title="More Actions" placement="top">
               {iconOrientation === "horizontal" ? <ThemedMoreHorizIcon width={20} height={20} /> : <ThemedMoreVertIcon width={20} height={20} />}
            </Tooltip>
         </IconButton>

         <Menu
            MenuListProps={{
               "aria-labelledby": "credential-management-button",
               className: "credential-management-menu"
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
         >
            <ProtectedButton permissionName={PERMISSIONS.CREATE_CREDENTIALS}>
               <MenuItem sx={{ display: !isEditor ? "none" : "block" }} onClick={() => handleActions("add")}>
                  Add
               </MenuItem>
            </ProtectedButton>

            {canEdit && <MenuItem onClick={() => handleActions("edit")}>Edit</MenuItem>}

            <MenuItem onClick={() => handleActions("view")}>View</MenuItem>

            {canShare && [
               <MenuItem key="share" onClick={() => handleActions("share")}>
                  Share
               </MenuItem>,
               <MenuItem key="unshare" onClick={() => handleActions("unshare")} disabled={credentialData?.share_user?.length === 0}>
                  Unshare
               </MenuItem>
            ]}

            {canDelete && <MenuItem onClick={() => handleActions("delete")}>Delete</MenuItem>}

            <ProtectedButton permissionName={PERMISSIONS.ACCESS_TO_CREDENTIAL_MANAGEMENT_PAGE}>
               <div>
                  <Divider sx={{ display: !isEditor && "none" }} />
                  <MenuItem sx={{ display: !isEditor && "none" }} onClick={handleOpenCredentialManagementPage}>
                     Manage
                  </MenuItem>
               </div>
            </ProtectedButton>
         </Menu>
      </>
   );
};

export default memo(CredentialManagementAction);
